import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  Timestamp,
  startAt,
  endAt,
  limit
} from 'firebase/firestore';
import { database as db } from '../config/firebase';

export interface MonthlyData {
  month: string;
  quotes: number;
  ebooks: number;
  audio: number;
  videos: number;
  users: number;
}

export interface AnalyticsData {
  monthlyData: MonthlyData[];
  totalQuotes: number;
  totalEbooks: number;
  totalAudio: number;
  totalVideos: number;
  totalUsers: number;
  publishedQuotes: number;
  publishedEbooks: number;
  publishedAudio: number;
  publishedVideos: number;
}

export interface LatestContent {
  latestQuote: any | null;
  latestEbook: any | null;
  latestAudio: any | null;
  latestVideo: any | null;
}

class AnalyticsService {
  private quotesCollection = 'quotes';
  private ebooksCollection = 'ebooks';
  private audioCollection = 'audiobooks';
  private videosCollection = 'videos';
  private usersCollection = 'users';

  async getAnalyticsData(): Promise<AnalyticsData> {
    try {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      sixMonthsAgo.setDate(1); // Start of month
      sixMonthsAgo.setHours(0, 0, 0, 0);

      const now = new Date();
      now.setHours(23, 59, 59, 999);

      // Get monthly data for the last 6 months
      const monthlyData = await this.getMonthlyData(sixMonthsAgo, now);
      
      // Get total counts
      const [totalQuotes, totalEbooks, totalAudio, totalVideos, totalUsers] = await Promise.all([
        this.getTotalCount(this.quotesCollection),
        this.getTotalCount(this.ebooksCollection),
        this.getTotalCount(this.audioCollection),
        this.getTotalCount(this.videosCollection),
        this.getTotalCount(this.usersCollection)
      ]);

      // Get published counts
      const [publishedQuotes, publishedEbooks, publishedAudio, publishedVideos] = await Promise.all([
        this.getPublishedCount(this.quotesCollection),
        this.getPublishedCount(this.ebooksCollection),
        this.getPublishedCount(this.audioCollection),
        this.getPublishedCount(this.videosCollection)
      ]);

      return {
        monthlyData,
        totalQuotes,
        totalEbooks,
        totalAudio,
        totalVideos,
        totalUsers,
        publishedQuotes,
        publishedEbooks,
        publishedAudio,
        publishedVideos
      };
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      throw error;
    }
  }

  private async getMonthlyData(startDate: Date, endDate: Date): Promise<MonthlyData[]> {
    const monthlyData: MonthlyData[] = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
      
      const monthName = currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      try {
        const [quotes, ebooks, audio, videos, users] = await Promise.all([
          this.getCountForMonth(this.quotesCollection, monthStart, monthEnd),
          this.getCountForMonth(this.ebooksCollection, monthStart, monthEnd),
          this.getCountForMonth(this.audioCollection, monthStart, monthEnd),
          this.getCountForMonth(this.videosCollection, monthStart, monthEnd),
          this.getCountForMonth(this.usersCollection, monthStart, monthEnd)
        ]);

        monthlyData.push({
          month: monthName,
          quotes,
          ebooks,
          audio,
          videos,
          users
        });
      } catch (error) {
        console.error(`Error fetching data for ${monthName}:`, error);
        monthlyData.push({
          month: monthName,
          quotes: 0,
          ebooks: 0,
          audio: 0,
          videos: 0,
          users: 0
        });
      }

      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    return monthlyData;
  }

  private async getCountForMonth(collectionName: string, startDate: Date, endDate: Date): Promise<number> {
    try {
      // For users collection, use a more robust approach that handles edge cases
      if (collectionName === this.usersCollection) {
        return await this.getUserCountForMonth(startDate, endDate);
      }

      const q = query(
        collection(db, collectionName),
        where('createdAt', '>=', Timestamp.fromDate(startDate)),
        where('createdAt', '<=', Timestamp.fromDate(endDate))
      );
      
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error: any) {
      console.error(`Error getting count for ${collectionName}:`, error);
      return 0;
    }
  }

  private async getUserCountForMonth(startDate: Date, endDate: Date): Promise<number> {
    try {
      // Use fallback method for users: Fetch all users and filter in memory
      // This is more reliable as it:
      // 1. Doesn't require composite indexes
      // 2. Handles missing createdAt fields gracefully
      // 3. Handles different timestamp formats
      const allUsersSnapshot = await getDocs(collection(db, this.usersCollection));
      let count = 0;
      
      allUsersSnapshot.forEach((doc) => {
        const data = doc.data();
        const createdAt = data.createdAt;
        
        if (createdAt) {
          let createdAtDate: Date | null = null;
          
          try {
            // Handle different timestamp formats
            if (createdAt.toDate && typeof createdAt.toDate === 'function') {
              // Firestore Timestamp with toDate method
              createdAtDate = createdAt.toDate();
            } else if (createdAt instanceof Date) {
              // Already a Date object
              createdAtDate = createdAt;
            } else if (createdAt.seconds) {
              // Firestore Timestamp object with seconds property
              createdAtDate = new Date(createdAt.seconds * 1000);
            } else if (typeof createdAt === 'number') {
              // Unix timestamp (milliseconds or seconds)
              createdAtDate = new Date(createdAt < 10000000000 ? createdAt * 1000 : createdAt);
            } else if (typeof createdAt === 'string') {
              // String timestamp (ISO 8601 or other formats)
              createdAtDate = new Date(createdAt);
              // Validate the date is valid
              if (isNaN(createdAtDate.getTime())) {
                createdAtDate = null;
              }
            }
            
            // Check if date is valid and within the month range
            if (createdAtDate && !isNaN(createdAtDate.getTime())) {
              if (createdAtDate >= startDate && createdAtDate <= endDate) {
                count++;
              }
            }
          } catch (error) {
            // Silently skip invalid dates
            console.debug(`Invalid createdAt for user ${doc.id}:`, createdAt);
          }
        }
      });
      
      return count;
    } catch (error: any) {
      console.error('Error getting user count for month:', error);
      return 0;
    }
  }

  private async getTotalCount(collectionName: string): Promise<number> {
    try {
      const snapshot = await getDocs(collection(db, collectionName));
      return snapshot.size;
    } catch (error) {
      console.error(`Error getting total count for ${collectionName}:`, error);
      return 0;
    }
  }

  private async getPublishedCount(collectionName: string): Promise<number> {
    try {
      const q = query(
        collection(db, collectionName),
        where('isPublished', '==', true)
      );
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error(`Error getting published count for ${collectionName}:`, error);
      return 0;
    }
  }

  async getLatestContent(): Promise<LatestContent> {
    try {
      const [latestQuote, latestEbook, latestAudio, latestVideo] = await Promise.all([
        this.getLatestItem(this.quotesCollection),
        this.getLatestItem(this.ebooksCollection),
        this.getLatestItem(this.audioCollection),
        this.getLatestItem(this.videosCollection)
      ]);

      return {
        latestQuote,
        latestEbook,
        latestAudio,
        latestVideo
      };
    } catch (error) {
      console.error('Error fetching latest content:', error);
      return {
        latestQuote: null,
        latestEbook: null,
        latestAudio: null,
        latestVideo: null
      };
    }
  }

  private async getLatestItem(collectionName: string): Promise<any | null> {
    try {
      const q = query(
        collection(db, collectionName),
        orderBy('createdAt', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      const data = doc.data();
      
      // Convert Firestore timestamps to Date objects
      const processedData = {
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
        publishedAt: data.publishedAt?.toDate ? data.publishedAt.toDate() : data.publishedAt,
      };

      return processedData;
    } catch (error) {
      console.error(`Error getting latest item from ${collectionName}:`, error);
      return null;
    }
  }
}

export const analyticsService = new AnalyticsService();
